import {
  ArrowRight,
  ChevronRight,
  Utilities,
} from "@@/elements/Icons/icons";
import Typography from "@@/elements/Typography";
import cn from "classnames";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Companies, MenuAriaLabels, MenuChoice } from "../headerProps";
import styles from "./DesktopNav.module.scss";

interface DesktopNavProps {
  navigationItems: MenuChoice[];
  company: Companies;
  ariaLabels?: MenuAriaLabels;
  searchOpen?: boolean;
}

const DEFAULT_ARIA_LABELS: MenuAriaLabels = {
  navLabel: "Main",
  openMenuLabel: "Open menu",
  closeMenuLabel: "Close menu",
  searchLabel: "Search",
  goBackLabel: "Go back",
  submenuLabel: "Submenu for {name}",
};

const DesktopNav = ({
  navigationItems,
  company,
  ariaLabels,
  searchOpen,
}: DesktopNavProps) => {
  const labels = { ...DEFAULT_ARIA_LABELS, ...ariaLabels };
  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null);
  const [openSubmenuIndex, setOpenSubmenuIndex] = useState<number | null>(null);
  const [positions, setPositions] = useState<number[]>([]);

  const triggerRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const submenuTriggerRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRefs = useRef<(HTMLDivElement | null)[]>([]);

  const calculatePositions = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const newPositions = triggerRefs.current.map((btn) => {
      if (!btn) return 0;
      const btnRect = btn.getBoundingClientRect();
      return btnRect.left - containerRect.left;
    });
    setPositions(newPositions);
  }, []);

  useLayoutEffect(() => {
    calculatePositions();
  }, [calculatePositions]);

  useEffect(() => {
    window.addEventListener("resize", calculatePositions);
    return () => window.removeEventListener("resize", calculatePositions);
  }, [calculatePositions]);

  const getSubmenuLabel = (name: string): string => {
    return labels.submenuLabel.replace("{name}", name);
  };

  const closeMenu = useCallback(() => {
    setOpenMenuIndex(null);
    setOpenSubmenuIndex(null);
  }, []);

  useEffect(() => {
    if (searchOpen) closeMenu();
  }, [searchOpen, closeMenu]);

  const toggleMenu = useCallback((index: number) => {
    setOpenSubmenuIndex(null);
    if (openMenuIndex === index) {
      setOpenMenuIndex(null);
    } else {
      setOpenMenuIndex(index);
    }
  }, [openMenuIndex]);

  const toggleSubmenu = useCallback((index: number) => {
    if (openSubmenuIndex === index) {
      setOpenSubmenuIndex(null);
    } else {
      setOpenSubmenuIndex(index);
    }
  }, [openSubmenuIndex]);

  const level3Ref = useRef<HTMLUListElement>(null);

  const focusFirstDropdownItem = useCallback((index: number) => {
    setTimeout(() => {
      const dropdown = dropdownRefs.current[index];
      if (dropdown) {
        const firstFocusable = dropdown.querySelector<HTMLElement>(
          "a, button",
        );
        firstFocusable?.focus();
      }
    }, 0);
  }, []);

  const focusFirstLevel3Item = useCallback(() => {
    setTimeout(() => {
      const list = level3Ref.current;
      if (list) {
        const firstFocusable = list.querySelector<HTMLElement>("a, button");
        firstFocusable?.focus();
      }
    }, 0);
  }, []);

  const handleTopLevelKeyDown = useCallback(
    (event: React.KeyboardEvent, index: number) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        setOpenSubmenuIndex(null);
        if (openMenuIndex === index) {
          setOpenMenuIndex(null);
        } else {
          setOpenMenuIndex(index);
          focusFirstDropdownItem(index);
        }
      }
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setOpenSubmenuIndex(null);
        setOpenMenuIndex(index);
        focusFirstDropdownItem(index);
      }
    },
    [openMenuIndex, focusFirstDropdownItem],
  );

  const handleSubmenuKeyDown = useCallback(
    (event: React.KeyboardEvent, childIndex: number) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        event.stopPropagation();
        if (openSubmenuIndex === childIndex) {
          setOpenSubmenuIndex(null);
        } else {
          setOpenSubmenuIndex(childIndex);
          focusFirstLevel3Item();
        }
      }
    },
    [openSubmenuIndex, focusFirstLevel3Item],
  );

  const handleLevel3KeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Tab" && event.shiftKey) {
        const list = level3Ref.current;
        if (!list) return;
        const firstItem = list.querySelector<HTMLElement>("a, button");
        if (document.activeElement === firstItem) {
          event.preventDefault();
          event.stopPropagation();
          if (openSubmenuIndex !== null && openMenuIndex !== null) {
            submenuTriggerRefs.current.get(`${openMenuIndex}-${openSubmenuIndex}`)?.focus();
            setOpenSubmenuIndex(null);
          }
        }
      }
      if (event.key === "Tab" && !event.shiftKey) {
        const list = level3Ref.current;
        if (!list) return;
        const items = list.querySelectorAll<HTMLElement>("a, button");
        const lastItem = items[items.length - 1];
        if (document.activeElement === lastItem) {
          event.preventDefault();
          event.stopPropagation();
          const triggerBtn = openSubmenuIndex !== null && openMenuIndex !== null
            ? submenuTriggerRefs.current.get(`${openMenuIndex}-${openSubmenuIndex}`)
            : null;
          const nextSubmenuItem = triggerBtn
            ?.closest("li")
            ?.nextElementSibling
            ?.querySelector<HTMLElement>("a, button");
          if (nextSubmenuItem) {
            setOpenSubmenuIndex(null);
            nextSubmenuItem.focus();
          } else if (openMenuIndex !== null) {
            const nextIndex = openMenuIndex + 1;
            if (nextIndex < triggerRefs.current.length) {
              triggerRefs.current[nextIndex]?.focus();
            } else {
              triggerRefs.current[0]?.focus();
            }
            closeMenu();
          }
        }
      }
    },
    [openSubmenuIndex, openMenuIndex, closeMenu],
  );

  const handleDropdownKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        if (openSubmenuIndex !== null && openMenuIndex !== null) {
          const triggerBtn = submenuTriggerRefs.current.get(`${openMenuIndex}-${openSubmenuIndex}`);
          setOpenSubmenuIndex(null);
          setTimeout(() => triggerBtn?.focus(), 0);
        } else if (openMenuIndex !== null) {
          const triggerBtn = triggerRefs.current[openMenuIndex];
          setOpenMenuIndex(null);
          setTimeout(() => triggerBtn?.focus(), 0);
        }
      }
      if (event.key === "Tab" && !event.shiftKey && openMenuIndex !== null) {
        const dropdown = dropdownRefs.current[openMenuIndex];
        if (!dropdown) return;
        const focusableItems = dropdown.querySelectorAll<HTMLElement>(
          "a[tabindex='0'], button[tabindex='0']",
        );
        const lastItem = focusableItems[focusableItems.length - 1];
        if (document.activeElement === lastItem) {
          event.preventDefault();
          const nextIndex = openMenuIndex + 1;
          if (nextIndex < triggerRefs.current.length) {
            triggerRefs.current[nextIndex]?.focus();
          } else {
            triggerRefs.current[0]?.focus();
          }
          closeMenu();
        }
      }
      if (event.key === "Tab" && event.shiftKey && openMenuIndex !== null) {
        const dropdown = dropdownRefs.current[openMenuIndex];
        if (!dropdown) return;
        const focusableItems = dropdown.querySelectorAll<HTMLElement>(
          "a[tabindex='0'], button[tabindex='0']",
        );
        if (document.activeElement === focusableItems[0]) {
          event.preventDefault();
          triggerRefs.current[openMenuIndex]?.focus();
          closeMenu();
        }
      }
    },
    [openSubmenuIndex, openMenuIndex, closeMenu],
  );

  useLayoutEffect(() => {
    if (openSubmenuIndex !== null && openMenuIndex !== null) {
      const dropdown = dropdownRefs.current[openMenuIndex];
      const level3 = level3Ref.current;
      if (dropdown && level3) {
        dropdown.style.minHeight = `${level3.scrollHeight}px`;
      }
    } else if (openMenuIndex !== null) {
      const dropdown = dropdownRefs.current[openMenuIndex];
      if (dropdown) {
        dropdown.style.minHeight = "";
      }
    }
  }, [openSubmenuIndex, openMenuIndex]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openMenuIndex === null) return;
      const target = event.target as Node;
      if (containerRef.current?.contains(target)) return;
      closeMenu();
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openMenuIndex, closeMenu]);

  const submenuClassNames = styles.submenu;

  const submenuLevel3ClassNames = cn(styles.submenu_level3, {
    [styles[`submenu_level3_${company}`]]: company,
  });

  const menuItemClassNames = cn(styles.menu_item, {
    [styles[`menu_item_${company}`]]: company,
  });

  const activeClassNames = cn(styles.menu_item_active, {
    [styles[`menu_item_active_${company}`]]: company,
  });

  const listingClassNames = cn(styles.listing_item, {
    [styles[`listing_item_${company}`]]: company,
  });

  return (
    <nav ref={containerRef} aria-label={labels.navLabel} className={styles.wrapper}>
      <ul className={styles.top_level}>
          {navigationItems.map((menuItem, index) => {
            const isOpen = openMenuIndex === index;
            const showExpanded = isOpen && openSubmenuIndex !== null;
            const dropdownId = `desktop-nav-dropdown-${index}`;
            const baseLeft = positions[index] || 0;
            const containerWidth = containerRef.current?.clientWidth || 0;
            const dropdownLeft = showExpanded
              ? Math.max(0, Math.min(baseLeft - 150, containerWidth - 630))
              : Math.max(0, Math.min(baseLeft, containerWidth - 315));

            return (
              <li key={index} className={styles.top_level_item}>
                <button
                  ref={(el) => { triggerRefs.current[index] = el; }}
                  type="button"
                  aria-expanded={isOpen}
                  aria-controls={dropdownId}
                  className={cn(styles.top_level_button, {
                    [styles.top_level_button_active]: isOpen,
                  })}
                  onClick={() => toggleMenu(index)}
                  onKeyDown={(e) => handleTopLevelKeyDown(e, index)}
                  data-menu-type="menu drawer"
                  data-menu-level={menuItem.depth}
                  data-menu-text={menuItem.name}
                  data-menu-path=""
                >
                  <Typography variant="menu" asElement="span">{menuItem.name}</Typography>
                </button>
                <div className={styles.dropdown_clip}>
                  <div
                    ref={(el) => { dropdownRefs.current[index] = el; }}
                    id={dropdownId}
                    aria-hidden={!isOpen}
                    className={cn(styles.dropdown, {
                      [styles.dropdown_open]: isOpen,
                      [styles.dropdown_expanded]: showExpanded,
                    })}
                    style={{ left: dropdownLeft }}
                    onKeyDown={isOpen ? handleDropdownKeyDown : undefined}
                  >
                  <ul className={submenuClassNames}>
                    {menuItem.children.map((child, childIndex) => (
                      <li key={childIndex} style={{ width: "100%" }}>
                        {child.children.length > 0 ? (
                          <>
                            <button
                              ref={(el) => {
                                if (el) submenuTriggerRefs.current.set(`${index}-${childIndex}`, el);
                              }}
                              type="button"
                              aria-expanded={isOpen && openSubmenuIndex === childIndex}
                              aria-controls={`desktop-nav-submenu-${index}-${childIndex}`}
                              className={cn(menuItemClassNames, {
                                [activeClassNames]: isOpen && openSubmenuIndex === childIndex,
                              })}
                              onClick={() => toggleSubmenu(childIndex)}
                              onKeyDown={(e) => handleSubmenuKeyDown(e, childIndex)}
                              tabIndex={isOpen ? 0 : -1}
                              data-menu-type="menu drawer"
                              data-menu-level={child.depth}
                              data-menu-text={child.name}
                              data-menu-path=""
                            >
                              <Typography variant="submenu1" asElement="span">
                                {child.name}
                              </Typography>
                              <ChevronRight aria-hidden="true" />
                            </button>
                            {isOpen && openSubmenuIndex === childIndex && (
                              <ul
                                ref={level3Ref}
                                id={`desktop-nav-submenu-${index}-${childIndex}`}
                                className={submenuLevel3ClassNames}
                                onKeyDown={handleLevel3KeyDown}
                              >
                                {child.children.map(
                                  (level3Item, level3Index) => (
                                    <li
                                      key={level3Index}
                                      style={{ width: "100%" }}
                                      className={cn({
                                        [styles.current_page]: level3Item.active,
                                      })}
                                    >
                                      {level3Index === 0 ? (
                                        <a
                                          href={level3Item.link}
                                          className={listingClassNames}
                                          aria-current={level3Item.active ? "page" : undefined}
                                          tabIndex={isOpen ? 0 : -1}
                                          data-menu-type="menu drawer"
                                          data-menu-level={level3Item.depth}
                                          data-menu-text={level3Item.name}
                                          data-menu-path={level3Item.link}
                                        >
                                          <Typography variant="submenu2">
                                            {level3Item.name}
                                          </Typography>
                                          <ArrowRight />
                                        </a>
                                      ) : (
                                        <a
                                          href={level3Item.link}
                                          target={level3Item.target}
                                          className={cn(styles.menu_item, {
                                            [styles[`menu_item_${company}`]]: company,
                                          })}
                                          aria-current={level3Item.active ? "page" : undefined}
                                          tabIndex={isOpen ? 0 : -1}
                                          data-menu-type="menu drawer"
                                          data-menu-level={level3Item.depth}
                                          data-menu-text={level3Item.name}
                                          data-menu-path={level3Item.link}
                                        >
                                          <Typography variant="submenu2">
                                            {level3Item.name}
                                          </Typography>
                                          {level3Item.target && (
                                            <Utilities className={styles.ext_link} />
                                          )}
                                        </a>
                                      )}
                                    </li>
                                  ),
                                )}
                              </ul>
                            )}
                          </>
                        ) : (
                          <a
                            href={child.link}
                            target={child.target}
                            className={menuItemClassNames}
                            aria-current={child.active ? "page" : undefined}
                            tabIndex={isOpen ? 0 : -1}
                            data-menu-type="menu drawer"
                            data-menu-level={child.depth}
                            data-menu-text={child.name}
                            data-menu-path={child.link}
                          >
                            <Typography variant="submenu1">
                              {child.name}
                            </Typography>
                            {child.target && (
                              <Utilities className={styles.ext_link} />
                            )}
                          </a>
                        )}
                      </li>
                    ))}
                  </ul>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
    </nav>
  );
};

export default DesktopNav;
